import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Text } from 'v3/components';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Spinner from 'react-bootstrap/Spinner';
import CreateChecklistWithLink from './CreateChecklistWithLink';
import LinkCreatedCard from './LinkCreatedCard';
import ListModal from './ListModal';
import CreateChecklist from './CreateChecklist';
import ResponseChecklistModal from '../PendingList/ResponseModal';
import PlateQuestionsModal from '../PlateQuestionsModal';
import { cooperplaceApi } from 'services/api';
import moment from 'moment';
import { useSnackbar } from 'v3/components/Snackbar';
import { buildTemplatePlateItems, toTemplatePlateAnswers } from '../plateQuestions';

function safeStr(v) {
  if (v === null || v === undefined) return '';
  return String(v);
}

function formatDate(value) {
  if (!value) return '—';
  const m = moment(value);
  return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : '—';
}

function normalizeGroupKey(group) {
  const slug = safeStr(group?.slug).trim();
  if (slug) return slug.toLowerCase();

  const name = safeStr(group?.name || group?.groupName || group?.label).trim();
  if (!name) return '';

  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .trim();
}

const MAINTENANCE_GROUP_KEYS = new Set(['maintenance', 'manutencao']);

function Backdrop({ children, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.35)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 900,
          maxHeight: '90vh',
          overflow: 'hidden',
          borderRadius: 16,
          background: '#FFFFFF',
          boxShadow: '0 20px 40px rgba(15,23,42,0.25)',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function DetailLoadingRow() {
  return (
    <div
      className="d-flex justify-content-center align-items-center py-4"
      role="status"
      aria-busy="true"
      style={{ minHeight: 220 }}
    >
      <Spinner animation="border" role="status" className="mr-3" style={{ color: '#9aa4ae' }} />
      <Text type="body" style={{ color: '#9aa4ae', fontWeight: 600 }}>
        Carregando
      </Text>
    </div>
  );
}

function normalizeModel(raw) {
  const groups = Array.isArray(raw?.checklistGroups) ? raw.checklistGroups : [];

  const normalizedGroups = groups
    .map((g) => {
      const label = safeStr(g?.name || g?.groupName || g?.label).trim();
      const key = normalizeGroupKey(g);
      return { id: g?.id, key, label, raw: g };
    })
    .filter((g) => g.key && g.label);

  const seen = new Set();
  const uniqueGroups = normalizedGroups.filter((g) => {
    if (seen.has(g.key)) return false;
    seen.add(g.key);
    return true;
  });

  const groupsKeys = uniqueGroups.map((g) => g.key);
  const groupLabel = uniqueGroups.length ? uniqueGroups.map((g) => g.label).join(', ') : '';

  const itemsCount = Array.isArray(raw?.checklistsQuestions)
    ? raw.checklistsQuestions.length
    : Array.isArray(raw?.questions)
    ? raw.questions.length
    : 0;

  const createdRaw = raw?.createdAt || raw?.created_at || raw?.created || '';

  return {
    id: raw?.id,
    title: raw?.name || 'Checklist',
    description: raw?.description || '',
    group: groupLabel,
    groups: uniqueGroups,
    groupsKeys,
    items: itemsCount,
    createdAt: formatDate(createdRaw),
    questions: raw?.checklistsQuestions || raw?.questions || [],
    raw,
  };
}

function normalizeCreatedRequest(raw) {
  const payload = raw?.data || raw;

  const id = payload?.id ?? payload?.requestId ?? payload?.request_id;
  const link = payload?.link || null;
  const linkUrl = link?.url || payload?.linkUrl || payload?.url || payload?.publicUrl || '';

  const questions =
    payload?.questions ??
    payload?.checklist?.questions ??
    payload?.singleChecklist?.questions ??
    [];

  return { id, link, linkUrl, questions, raw: payload };
}

export default function RequestChecklistModal({ open, onClose, onCreated }) {
  const snackbar = useSnackbar();
  const [step, setStep] = useState('select');
  const [selectedModel, setSelectedModel] = useState(null);
  const [groupFilter, setGroupFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState('portal');
  const [portalOption, setPortalOption] = useState('start');
  const [anonymous, setAnonymous] = useState(false);
  const [expireDays, setExpireDays] = useState(null);
  const [singleUse, setSingleUse] = useState(false);
  const [linkName, setLinkName] = useState('');
  const [linkNameTouched, setLinkNameTouched] = useState(false);
  const [models, setModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [didInitialLoad, setDidInitialLoad] = useState(false);
  const [createdRequest, setCreatedRequest] = useState(null);
  const respondSucceededRef = useRef(false);
  const [creating, setCreating] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState('single');
  const [maintenanceDays, setMaintenanceDays] = useState(0);
  const [plateModalOpen, setPlateModalOpen] = useState(false);
  const [plateModalItems, setPlateModalItems] = useState([]);
  const [plateModalError, setPlateModalError] = useState('');

  const generatedLinkUrl = useMemo(
    () => createdRequest?.link?.url || createdRequest?.linkUrl || '',
    [createdRequest]
  );

  useEffect(() => {
    if (!open) return;

    setDidInitialLoad(false);
    setLoadingModels(false);
    setModels([]);
    setGroupFilter('all');
    setSearch('');
    setStep('select');
    setSelectedModel(null);
    setMode('portal');
    setPortalOption('start');
    setAnonymous(false);
    setExpireDays(null);
    setSingleUse(false);
    setLinkName('');
    setLinkNameTouched(false);
    setCreatedRequest(null);
    setCreating(false);
    setMaintenanceMode('single');
    setMaintenanceDays(0);
    setPlateModalOpen(false);
    setPlateModalItems([]);
    setPlateModalError('');
  }, [open]);

  const loadModels = useCallback(async () => {
    try {
      setLoadingModels(true);

      const { data } = await cooperplaceApi.get('/single-checklist/requests', {
        params: {
          checklistName: search?.trim() || undefined,
          page: 1,
          perPage: 50,
        },
      });

      const arr = data?.data || data || [];
      const normalized = Array.isArray(arr) ? arr.map(normalizeModel) : [];
      setModels(normalized);
    } catch (e) {
      setModels([]);
    } finally {
      setLoadingModels(false);
      setDidInitialLoad(true);
    }
  }, [search]);

  useEffect(() => {
    if (!open) return;
    loadModels();
  }, [open, loadModels]);

  const isMaintenance = useMemo(() => {
    const keys = Array.isArray(selectedModel?.groupsKeys) ? selectedModel.groupsKeys : [];
    return keys.some((key) => MAINTENANCE_GROUP_KEYS.has(String(key)));
  }, [selectedModel]);

  const linkNameError =
    linkNameTouched && !linkName.trim()
      ? 'Informe um nome para o checklist do link.'
      : '';

  const groupTabs = useMemo(() => {
    const map = new Map();
    (models || []).forEach((m) => {
      (m?.groups || []).forEach((g) => {
        if (g?.key && g?.label && !map.has(g.key)) map.set(g.key, g.label);
      });
    });
    const dynamic = Array.from(map.entries()).map(([key, label]) => ({ key, label }));
    return [{ key: 'all', label: 'Todos' }].concat(dynamic);
  }, [models]);

  const selectedGroups = useMemo(() => {
    if (groupFilter === 'all') return [];
    return Array.isArray(groupFilter) ? groupFilter.map(String) : [String(groupFilter)];
  }, [groupFilter]);

  const filteredModels = useMemo(() => {
    if (!selectedGroups.length) return models;
    return (models || []).filter((m) => {
      const keys = Array.isArray(m?.groupsKeys) ? m.groupsKeys.map(String) : [];
      return selectedGroups.some((g) => keys.includes(g));
    });
  }, [models, selectedGroups]);

  useEffect(() => {
    if (groupFilter === 'all') return;

    const selected = Array.isArray(groupFilter) ? groupFilter : [groupFilter];
    const validKeys = new Set(groupTabs.map((t) => String(t.key)));

    const cleaned = selected.map(String).filter((k) => validKeys.has(k));
    if (!cleaned.length) setGroupFilter('all');
    else if (cleaned.length !== selected.length) setGroupFilter(cleaned);
  }, [groupFilter, groupTabs]);

  function resetAndClose() {
    setStep('select');
    setSelectedModel(null);
    setCreatedRequest(null);
    setGroupFilter('all');
    setSearch('');
    setMode('portal');
    setPortalOption('start');
    setAnonymous(false);
    setExpireDays(7);
    setSingleUse(true);
    setMaintenanceMode('single');
    setMaintenanceDays(0);
    setPlateModalOpen(false);
    setPlateModalItems([]);
    setPlateModalError('');

    if (onClose) onClose();
  }

  function handleSelectModel(model) {
    setSelectedModel(model);
    setCreatedRequest(null);
    setStep('config');
    setMode('portal');
    setPortalOption('start');

    const raw = model?.raw || {};
    const baseDays =
      Number(raw?.expirationDays) ||
      Number(raw?.singleChecklistConfig?.expirationDays) ||
      0;
    setMaintenanceDays(baseDays);
    setMaintenanceMode('single');
  }

  function handleCopyLink(url = generatedLinkUrl) {
    if (!url) return;

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url);
      return;
    }

    const el = document.createElement('textarea');
    el.value = url;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  }

  function handleBack() {
    if (step === 'respond') {
      setStep('config');
      setMode('portal');
      return;
    }

    if (step === 'linkCreated') {
      setStep('config');
      setMode('link');
      return;
    }

    if (step === 'config') {
      setStep('select');
      setSelectedModel(null);
      setCreatedRequest(null);
      setMode('portal');
      return;
    }

    resetAndClose();
  }

  async function createRequest({ withLink, notifyParent = true, plateAnswers = [] }) {
    const checklistId = Number(selectedModel?.id ?? selectedModel?.raw?.id);
    if (!checklistId || Number.isNaN(checklistId)) return null;

    const trimmedLinkName = linkName.trim();
    let body = withLink
      ? {
          name: trimmedLinkName,
          link: {
            isAnonymous: !!anonymous,
            responseUnique: !!singleUse,
            expirationDays: Number(expireDays) || 0,
          },
          ...(plateAnswers.length ? { plateAnswers } : null),
        }
      : {};

    if (!withLink) {
      const daysValue = Number(maintenanceDays) || 0;
      const recurring = maintenanceMode === 'recurring';

      body.expirationDays = isMaintenance ? daysValue : 0;
      body.isRecurring = isMaintenance ? recurring : false;
    }

    try {
      setCreating(true);

      const { data } = await cooperplaceApi.post(`/single-checklist/requests/${checklistId}`, body);
      const normalized = normalizeCreatedRequest(data?.data || data || {});
      setCreatedRequest(normalized);
      if (notifyParent && onCreated) onCreated(normalized);
      return normalized;
    } catch (e) {
      const message =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        'Não foi possível criar o checklist.';
      setPlateModalError(String(message));
      snackbar.show(<Text>{message}</Text>, { type: 'error' });
      return null;
    } finally {
      setCreating(false);
    }
  }

  async function handleConfirmPortal() {
    const created = await createRequest({ withLink: false });
    if (!created) return;

    if (portalOption === 'start') {
      setStep('respond');
      return;
    }
    resetAndClose();
  }

  async function finishGenerateLink(plateAnswers = []) {
    const created = await createRequest({
      withLink: true,
      notifyParent: false,
      plateAnswers,
    });
    if (!created?.id) return;

    try {
      setCreating(true);
      const { data } = await cooperplaceApi.get(`/single-checklist/${created.id}`);
      const full = normalizeCreatedRequest(data?.data || data || {});

      setCreatedRequest(full);
      setMode('link');
      setStep('linkCreated');
      setPlateModalOpen(false);
      setPlateModalError('');
    } catch (e) {
      if (created?.link) {
        setMode('link');
        setStep('linkCreated');
        setPlateModalOpen(false);
        setPlateModalError('');
      }
    } finally {
      setCreating(false);
    }
  }

  async function handleGenerateLink() {
    if (!linkName.trim()) {
      setLinkNameTouched(true);
      return;
    }

    const plateItems = buildTemplatePlateItems(selectedModel?.questions);
    if (plateItems.length) {
      setPlateModalItems(plateItems);
      setPlateModalError('');
      setPlateModalOpen(true);
      return;
    }

    await finishGenerateLink();
  }

  async function handleConfirmPlateQuestions(selections) {
    setPlateModalError('');
    await finishGenerateLink(toTemplatePlateAnswers(selections));
  }

  function handleCloseAfterLink() {
    if (onCreated && createdRequest) onCreated(createdRequest);
    resetAndClose();
  }

  function renderHeaderTitle() {
    if (step === 'select') return 'Solicitar Checklist';
    return selectedModel?.title || 'Checklist';
  }

  function renderHeaderSubtitle() {
    if (step === 'select') return 'Selecione um checklist para responder ou enviar via link';
    return 'Escolha como deseja prosseguir com este checklist';
  }

  const linkBody =
    step === 'linkCreated' ? (
      <LinkCreatedCard
        link={createdRequest?.link}
        onClose={handleCloseAfterLink}
        onCopy={(url) => handleCopyLink(url)}
      />
    ) : (
      <CreateChecklistWithLink
        linkName={linkName}
        setLinkName={setLinkName}
        linkNameTouched={linkNameTouched}
        setLinkNameTouched={setLinkNameTouched}
        linkNameError={linkNameError}
        anonymous={anonymous}
        setAnonymous={setAnonymous}
        expireDays={expireDays}
        setExpireDays={setExpireDays}
        singleUse={singleUse}
        setSingleUse={setSingleUse}
        onCancel={handleBack}
        onGenerate={handleGenerateLink}
      />
    );

  function renderConfigStep() {
    const recurringNeedsDays =
      isMaintenance && maintenanceMode === 'recurring' && Number(maintenanceDays) <= 0;

    return (
      <CreateChecklist
        mode={mode}
        setMode={(next) => {
          setMode(next);
          if (next === 'portal' && step === 'linkCreated') {
            setStep('config');
          }
        }}
        isMaintenance={isMaintenance}
        maintenanceMode={maintenanceMode}
        setMaintenanceMode={setMaintenanceMode}
        maintenanceDays={maintenanceDays}
        setMaintenanceDays={setMaintenanceDays}
        portalOption={portalOption}
        setPortalOption={setPortalOption}
        onBack={handleBack}
        onConfirmPortal={handleConfirmPortal}
        confirmLabel="Criar"
        confirmDisabled={recurringNeedsDays}
        LinkBody={linkBody}
        loading={creating}
        loadingLabel={mode === 'link' ? 'Gerando link...' : 'Criando checklist...'}
      />
    );
  }

  const responseQuestions =
    createdRequest?.questions?.length ? createdRequest.questions : selectedModel?.questions || [];

  if (!open) return null;

  return (
    <Backdrop onClose={resetAndClose}>
      <div style={{ padding: '1.5rem 1.75rem' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '0.5rem',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <DescriptionOutlinedIcon style={{ fontSize: 20, color: '#1F2937' }} />
              <Text type="header" color="dark" style={{ fontWeight: 700 }}>
                {renderHeaderTitle()}
              </Text>
            </div>

            <div style={{ width: '100%', height: 1, backgroundColor: '#D1D5DB', margin: '8px 0' }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <InfoOutlinedIcon style={{ fontSize: 14, color: '#6B7280' }} />
              <Text color="gray" style={{ fontSize: 10, lineHeight: '14px', margin: 0 }}>
                {renderHeaderSubtitle()}
              </Text>
            </div>
          </div>

          <button
            type="button"
            onClick={resetAndClose}
            style={{
              border: 'none',
              background: 'transparent',
              fontSize: 20,
              cursor: 'pointer',
              lineHeight: 1,
              marginLeft: 12,
              color: '#111827',
            }}
            aria-label="Fechar"
            title="Fechar"
          >
            ✕
          </button>
        </div>

        <div style={{ marginTop: '1.5rem' }}>
          {step === 'select' ? (
            !didInitialLoad ? (
              <DetailLoadingRow />
            ) : (
              <ListModal
                models={filteredModels}
                loading={loadingModels}
                groupTabs={groupTabs}
                groupFilter={groupFilter}
                onChangeGroupFilter={(v) => setGroupFilter(v)}
                search={search}
                onChangeSearch={(v) => setSearch(v)}
                onSelectModel={handleSelectModel}
                onRefresh={loadModels}
              />
            )
          ) : step === 'respond' ? (
            <ResponseChecklistModal
              open={true}
              id={createdRequest?.id}
              onClose={() => {
                if (respondSucceededRef.current) {
                  respondSucceededRef.current = false;
                  resetAndClose();
                  return;
                }
                handleBack();
              }}
              checklistTitle={selectedModel?.title}
              // Com o checklist criado, o modal carrega as perguntas da resposta (ids reais);
              // as perguntas do modelo só servem de fallback sem id.
              questions={createdRequest?.id ? null : responseQuestions}
              onSuccess={() => {
                respondSucceededRef.current = true;
              }}
              onAnnul={() => {
                resetAndClose();
              }}
            />
          ) : (
            selectedModel && renderConfigStep()
          )}
        </div>

        <PlateQuestionsModal
          open={plateModalOpen}
          items={plateModalItems}
          loading={creating}
          error={plateModalError}
          onClose={() => {
            if (creating) return;
            setPlateModalOpen(false);
            setPlateModalError('');
          }}
          onConfirm={handleConfirmPlateQuestions}
        />
      </div>
    </Backdrop>
  );
}
